import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHead, Modal, Stat } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, useStore } from "../lib/store";
import { useCan } from "../lib/auth";
import { BUILDING_TYPES } from "../lib/program";
import { downloadProposalPdf } from "../lib/pdf";
import { fmtDate } from "../lib/format";
import type { Lead, LeadStatus } from "../lib/types";

/* The pipeline before a project exists: enquiry → proposal → won / lost. */
const COLUMNS: { key: LeadStatus; label: string; hue: string }[] = [
  { key: "enquiry", label: "Enquiry", hue: "var(--ink-3)" },
  { key: "proposal", label: "Proposal out", hue: "var(--warn)" },
  { key: "won", label: "Won", hue: "var(--ok)" },
  { key: "lost", label: "Lost", hue: "var(--danger)" },
];

export default function Pipeline() {
  const { leads = [] } = useStore();
  const canEdit = useCan("projects");
  const nav = useNavigate();
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState<Lead | null>(null);

  const won = leads.filter((l) => l.status === "won");
  const lost = leads.filter((l) => l.status === "lost");
  const live = leads.filter((l) => l.status === "enquiry" || l.status === "proposal");
  const decided = won.length + lost.length;
  const winRate = decided ? Math.round((won.length / decided) * 100) : 0;
  const pipelineValue = live.reduce((s, l) => s + (l.fee ?? 0), 0);
  const cur = leads[0]?.currency ?? "USD";

  function convert(l: Lead) {
    const id = actions.createProject({
      name: l.name, type: l.type ?? "Private Residence", location: l.location ?? "", area: "",
      budget: l.budget ?? 0, currency: l.currency ?? "USD",
      clientName: l.clientName, clientCompany: l.company ?? "", clientEmail: l.email ?? "",
    });
    actions.updateLead(l.id, { status: "won", projectId: id, decidedOn: new Date().toISOString().slice(0, 10) });
    nav(`/studio/${id}/dashboard`);
  }

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "34px clamp(18px,4vw,44px) 80px" }}>
      <PageHead kicker="Studio" title="Pipeline" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 20 }}>
        Work you're chasing, before it becomes a project — enquiries, proposals out, and what you won or lost. Convert a win into a live project in one step.
      </p>

      <div className="grid reveal" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 24 }}>
        <Stat label="Live opportunities">{live.length}</Stat>
        <Stat label="Pipeline fee value">{money(pipelineValue, cur)}</Stat>
        <Stat label="Win rate" sub={decided ? `${won.length} of ${decided} decided` : "nothing decided yet"}>{decided ? `${winRate}%` : "—"}</Stat>
        <Stat label="Won" sub={`${money(won.reduce((s, l) => s + (l.fee ?? 0), 0), cur)} in fees`}>{won.length}</Stat>
      </div>

      <div className="row between wrap reveal" style={{ gap: 10, marginBottom: 16 }}>
        <span className="dim" style={{ fontSize: 13 }}>{leads.length} opportunit{leads.length === 1 ? "y" : "ies"} tracked</span>
        {canEdit && <button className="btn accent" onClick={() => setAdd(true)}><Icon.plus width={15} />New enquiry</button>}
      </div>

      {leads.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">Nothing in the pipeline</div>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>Log an enquiry the moment it arrives — then track it through to a proposal and a decision, so you can see your real conversion rate.</p>
          {canEdit && <button className="btn accent" style={{ marginTop: 14 }} onClick={() => setAdd(true)}><Icon.plus width={15} />Log the first enquiry</button>}
        </div>
      ) : (
        <div className="grid reveal" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 14, alignItems: "start" }}>
          {COLUMNS.map((col) => {
            const items = leads.filter((l) => l.status === col.key);
            return (
              <div key={col.key} className="card" style={{ overflow: "hidden" }}>
                <div className="row between" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}>
                  <span className="row" style={{ gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 8, background: col.hue }} /><span className="kicker">{col.label}</span></span>
                  <span className="dim" style={{ fontSize: 12 }}>{items.length}</span>
                </div>
                <div className="col" style={{ gap: 0 }}>
                  {items.length === 0 && <div className="dim" style={{ padding: "16px 14px", fontSize: 12.5 }}>Nothing here.</div>}
                  {items.map((l, i) => (
                    <div key={l.id} style={{ padding: "12px 14px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                      <div className="row between" style={{ gap: 8 }}>
                        <span className="mono dim" style={{ fontSize: 10.5 }}>{l.ref}</span>
                        {l.fee ? <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{money(l.fee, l.currency ?? "USD")}</span> : null}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 3 }}>{l.name}</div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{[l.clientName, l.type, l.location].filter(Boolean).join(" · ")}</div>
                      {l.lostReason && <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>Lost: {l.lostReason}</div>}
                      {canEdit && (
                        <div className="row wrap" style={{ gap: 6, marginTop: 9 }}>
                          {l.status === "enquiry" && <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.setLeadStatus(l.id, "proposal")}>Proposal sent</button>}
                          {l.status === "proposal" && <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.setLeadStatus(l.id, "lost", prompt("Why was it lost? (optional)") ?? undefined)}>Lost</button>}
                          {canEdit && <button className="chip" style={{ fontSize: 11 }} onClick={() => downloadProposalPdf(l, null)}>Proposal PDF</button>}
                          {(l.status === "enquiry" || l.status === "proposal") && <button className="chip on" style={{ fontSize: 11, background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" }} onClick={() => convert(l)}>Won → create project</button>}
                          {l.status === "won" && l.projectId && <button className="chip" style={{ fontSize: 11 }} onClick={() => nav(`/studio/${l.projectId}/dashboard`)}>Open project</button>}
                          <button className="chip" style={{ fontSize: 11 }} onClick={() => setEdit(l)}>Edit</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {add && <LeadModal onClose={() => setAdd(false)} />}
      {edit && <LeadModal lead={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function LeadModal({ lead, onClose }: { lead?: Lead; onClose: () => void }) {
  const [name, setName] = useState(lead?.name ?? "");
  const [clientName, setClientName] = useState(lead?.clientName ?? "");
  const [company, setCompany] = useState(lead?.company ?? "");
  const [email, setEmail] = useState(lead?.email ?? "");
  const [phone, setPhone] = useState(lead?.phone ?? "");
  const [type, setType] = useState(lead?.type ?? BUILDING_TYPES[0]);
  const [location, setLocation] = useState(lead?.location ?? "");
  const [budget, setBudget] = useState(String(lead?.budget ?? ""));
  const [fee, setFee] = useState(String(lead?.fee ?? ""));
  const [source, setSource] = useState(lead?.source ?? "");
  const [note, setNote] = useState(lead?.note ?? "");

  function save() {
    const data = { name, clientName, company, email, phone, type, location, budget: Number(budget) || undefined, fee: Number(fee) || undefined, source, note };
    if (lead) actions.updateLead(lead.id, data as any); else actions.createLead(data);
    onClose();
  }
  return (
    <Modal open onClose={onClose} title={lead ? `Edit ${lead.ref}` : "New enquiry"} wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1.4 }}><label>Opportunity</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Meetinghouse — Berbice" /></div>
          <div className="field" style={{ flex: 1 }}><label>Source</label><input className="input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Referral, repeat, website…" /></div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Client</label><input className="input" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Name" /></div>
          <div className="field" style={{ flex: 1 }}><label>Company</label><input className="input" value={company} onChange={(e) => setCompany(e.target.value)} /></div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Email</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div className="field" style={{ flex: 1 }}><label>Phone</label><input className="input mono" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Type</label><select className="select" value={type} onChange={(e) => setType(e.target.value)}>{BUILDING_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="field" style={{ flex: 1 }}><label>Location</label><input className="input" value={location} onChange={(e) => setLocation(e.target.value)} /></div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Construction budget</label><input className="input mono" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} /></div>
          <div className="field" style={{ flex: 1 }}><label>Proposed fee</label><input className="input mono" type="number" value={fee} onChange={(e) => setFee(e.target.value)} /></div>
        </div>
        <div className="field"><label>Note</label><textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        {lead && <div className="dim" style={{ fontSize: 11.5 }}>Logged {fmtDate(lead.createdOn)}{lead.proposalSentOn ? ` · proposal ${fmtDate(lead.proposalSentOn)}` : ""}</div>}
        <div className="row between">
          {lead ? <button className="btn ghost sm" style={{ color: "var(--danger)" }} onClick={() => { if (confirm(`Remove ${lead.ref}?`)) { actions.removeLead(lead.id); onClose(); } }}>Remove</button> : <span />}
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn accent" disabled={!name.trim() || !clientName.trim()} onClick={save}><Icon.check width={15} />{lead ? "Save" : "Log enquiry"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
